import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { formShape, createForm } from 'rc-form';
import styled from 'styled-components';
import TextField from '../SolvPatternLibrary/TextField';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import { isEmptyString, stripNonNumeric } from '../../core/util/string';
import { isEmptyObject, safeGet, isUndefined } from '../../core/util/object';
import { UNFREEZE_TIME_MINUTES } from '../../constants/phoneLoginForm';
import { PHONE_LOGIN_SUBMIT_ERROR } from '../../core/analytics/events';
import { analyticsTrackEvent, analyticsTrackFormErrors } from '../../core/analytics';
import { isValidPhone } from '../../core/util/phoneNumber';
import { colors } from '../../constants/colors';
import { fontFamily, fontSize } from '../../constants/text';
import { phoneNumberValidator } from '../OtpCodeLoginForm/util/validator';
import { PWA_ADD_TO_HOME_SCREEN } from '../App/util/tracking';
import { MaskedPhoneTextField } from '../SolvPatternLibrary/MaskedTextField';
import { formatLocationName } from '../../core/util/location';
import { getCurrentPath } from '../../core/util/url';
import { isCdp } from '../../core/opengraph';

const ACCOUNT_NOT_FOUND = 'Account not found';
const VERIFICATION_STATUS_WAITING = 'vsw';
const VERIFICATION_STATUS_SUBMITTING = 'vss';
const VERIFICATION_STATUS_NOT_VERIFIED = 'vsnv';
const PHONE_FIELD_NAME = 'phone';
const LAST_NAME_FIELD_NAME = 'lastName';

const Title = styled.h3`
  color: ${colors.blueLoadingScreen};
  text-align: center;
  font-size: ${fontSize.medium};
  font-family: ${fontFamily.bold};
  letter-spacing: -0.03px;
  line-height: 22px;
  margin-bottom: 5px;
`;

const CTA = styled(Button)`
  margin-top: 28px;
  padding: 0;
  height: 56px;
`;

const Form = styled.form`
  width: 100%;
`;

const LastNameTextField = styled(TextField)`
  margin-bottom: 10px;
  height: 52px;
`;

const LastNameInputLabel = styled.p`
  margin-top: 30px;
  margin-bottom: 0;
  color: ${colors.lightText};
  font-size: ${fontSize.default};
  letter-spacing: -0.03px;
`;

const ClinicDisclaimer = styled.p`
  font-size: ${fontSize.default};
  color: ${colors.lightText};
`;

const HighlightedName = styled.span`
  color: ${colors.blueLoadingScreen};
`;

class PhoneLoginForm extends Component {
  constructor(props) {
    super(props);
    this.verificationPromise = Promise.resolve();
    this.state = {
      errors: {},
      submitted: false,
      submitting: false,
      submitButtonDisabled: true,
    };
  }

  componentDidMount = () => {
    const { initialValues, resetLoginData, setVerificationStatus, form, verifyPhone } = this.props;
    resetLoginData();
    setVerificationStatus(VERIFICATION_STATUS_WAITING);
    const phoneFieldValue = initialValues[PHONE_FIELD_NAME];
    if (!isEmptyString(phoneFieldValue)) {
      this.verificationPromise = verifyPhone(phoneFieldValue);
      form.validateFields(this.enableFormSubmissionIfNoErrors);
    }
  };

  onFieldChange = (value, fieldName) => {
    const { form } = this.props;
    const { errors } = this.state;

    if (isValidPhone(value) && fieldName === PHONE_FIELD_NAME) {
      this.onPhoneFieldValid(value);
    }

    this.setState({ errors: { ...errors, [fieldName]: false } });
    form.setFieldsValue({ [fieldName]: value });
    form.validateFields(this.enableFormSubmissionIfNoErrors);
  };

  onBlur = fieldName => {
    const { form } = this.props;
    const value = form.getFieldValue(PHONE_FIELD_NAME);
    // SOLV-4645: if on-blur on a empty field, don't validate
    if (isEmptyString(value)) {
      return;
    }
    form.validateFields([fieldName], {}, errors => {
      this.enableFormSubmissionIfNoErrors(errors);
      if (isEmptyObject(errors)) {
        this.setState({ errors: { [fieldName]: false } });
      } else {
        this.setState({ errors: { [fieldName]: errors[fieldName] } });
      }
    });
  };

  onPhoneFieldValid = rawValue => {
    const { verificationStatus, setVerificationStatus, verifyPhone } = this.props;
    const value = stripNonNumeric(String(rawValue));

    if (verificationStatus !== VERIFICATION_STATUS_WAITING) {
      setVerificationStatus(VERIFICATION_STATUS_WAITING);
    }

    if (verificationStatus === VERIFICATION_STATUS_WAITING) {
      this.verificationPromise = verifyPhone(value);
    }
  };

  onSubmit = e => {
    e && e.preventDefault();
    const { handleFormSubmit, form } = this.props;

    this.setState({ submitting: true });
    const clickedWhileVerificationSubmitting =
      this.props.verificationStatus === VERIFICATION_STATUS_SUBMITTING;
    form.validateFields((errors, values) => {
      if (isEmptyObject(errors)) {
        return this.verificationPromise.then(() => {
          if (
            clickedWhileVerificationSubmitting &&
            this.props.verificationStatus === VERIFICATION_STATUS_NOT_VERIFIED
          ) {
            this.setState({ submitting: false });
          } else {
            handleFormSubmit(values, this.onSubmitError);
          }
        });
      }

      this.setState({ submitting: false });
      analyticsTrackFormErrors(PHONE_LOGIN_SUBMIT_ERROR, {
        errors,
      });
      return null;
    });
  };

  onSubmitError = error =>
    this.setState({ submitting: false, errors: { [PHONE_FIELD_NAME]: error } });

  onClickAddToHomeScreen = () => {
    window.deferredA2HSPrompt && window.deferredA2HSPrompt.prompt();
    analyticsTrackEvent(PWA_ADD_TO_HOME_SCREEN, {
      action: 'solv prompt',
      origin: 'phone login form',
    });
  };

  getErrorState = () => {
    const { errors } = this.state;
    return Boolean(errors[PHONE_FIELD_NAME]);
  };

  getPhoneFieldHelperText = () => {
    const { login } = this.props;
    const { errors } = this.state;

    if (!isEmptyObject(errors[PHONE_FIELD_NAME])) {
      const errorDetail = safeGet(login)('login.login.otp_error_detail');
      const accountFrozen = safeGet(login)('login.accountFrozen');
      if (errorDetail && errorDetail !== ACCOUNT_NOT_FOUND) {
        return "Oops. That didn't work. Check the code and try again.";
      } else if (accountFrozen) {
        return (
          'You exceeded your log in attempts. \n' +
          `Please try again in ${UNFREEZE_TIME_MINUTES} minutes.`
        );
      }
    }

    return '';
  };

  enableFormSubmissionIfNoErrors = errors =>
    isEmptyObject(errors)
      ? this.state.submitButtonDisabled && this.setState({ submitButtonDisabled: false })
      : this.state.submitButtonDisabled || this.setState({ submitButtonDisabled: true });

  isOTPCodeError = () =>
    this.props.login &&
    this.props.login.login &&
    this.props.login.login.otp_error &&
    this.props.login.login.otp_error_detail !== ACCOUNT_NOT_FOUND;

  isSendCodeButtonDisabled = () => {
    const { form } = this.props;
    const { submitting, submitButtonDisabled } = this.state;
    const phone = form.getFieldValue(PHONE_FIELD_NAME);

    return submitButtonDisabled || submitting || !isValidPhone(phone);
  };

  requiresPhoneVerification = () =>
    this.props.verificationStatus === VERIFICATION_STATUS_NOT_VERIFIED;

  shouldShowClinicDisclaimer = () => {
    const { location } = this.props;
    return !isCdp(getCurrentPath()) && !isEmptyObject(location) && location.displayNamePrimary;
  };

  render() {
    const { errors } = this.state;
    const { form, initialValues, location } = this.props;

    return (
      <Fragment>
        <Title>To continue, enter your phone number:</Title>
        <Form onSubmit={this.onSubmit}>
          <MaskedPhoneTextField
            {...form.getFieldProps(PHONE_FIELD_NAME, {
              rules: [
                {
                  required: true,
                  validator: phoneNumberValidator,
                },
              ],
              initialValue: initialValues[PHONE_FIELD_NAME] || '',
            })}
            fullWidth
            label="Mobile phone number"
            placeholder="Enter mobile phone number"
            autoFocus={!initialValues[PHONE_FIELD_NAME]}
            type="tel"
            pattern="[0-9]*"
            autofill="tel-national"
            data-hj-suppress
            error={this.getErrorState()}
            onChange={({ target: { value } }) => this.onFieldChange(value, PHONE_FIELD_NAME)}
            onBlur={() => this.onBlur(PHONE_FIELD_NAME)}
            helperText={this.getPhoneFieldHelperText()}
            inputProps={{
              'data-testid': 'phone',
            }}
          />
          {this.requiresPhoneVerification() && (
            <Fragment>
              <LastNameInputLabel>Looks like you're new to Solv, welcome!</LastNameInputLabel>
              <LastNameTextField
                {...form.getFieldProps(LAST_NAME_FIELD_NAME, {
                  rules: [{ required: true }],
                  initialValue: initialValues[LAST_NAME_FIELD_NAME] || '',
                })}
                fullWidth
                label="Patient's last name"
                placeholder="Enter patient's last name"
                autofill="family-name"
                data-hj-suppress
                error={Boolean(errors[LAST_NAME_FIELD_NAME])}
                onChange={({ target: { value } }) =>
                  this.onFieldChange(value, LAST_NAME_FIELD_NAME)
                }
                onBlur={() => this.onBlur(LAST_NAME_FIELD_NAME)}
                inputProps={{ 'data-testid': 'lastName' }}
              />
            </Fragment>
          )}
          <CTA
            text="Send verification code"
            variation={ButtonVariations.pinkPrimary}
            type="submit"
            width="100%"
            disabled={this.isSendCodeButtonDisabled()}
            data-testid="phoneLoginFormSubmit"
          />
          {this.shouldShowClinicDisclaimer() && (
            <ClinicDisclaimer>
              <HighlightedName>{formatLocationName(location)}</HighlightedName> uses{' '}
              <HighlightedName>Solv</HighlightedName> to make it easy for you to manage your visits
              and insurance.
            </ClinicDisclaimer>
          )}
        </Form>
      </Fragment>
    );
  }
}

PhoneLoginForm.propTypes = {
  form: formShape,
  handleFormSubmit: PropTypes.func.isRequired,
  isBookingWidgetEmbedded: PropTypes.bool,
  hideLoginModal: PropTypes.func.isRequired,
  setVerificationStatus: PropTypes.func.isRequired,
  resetLoginData: PropTypes.func.isRequired,
  verificationStatus: PropTypes.string,
  verifyPhone: PropTypes.func.isRequired,
  login: PropTypes.object.isRequired,
  location: PropTypes.object,
  initialValues: PropTypes.object,
  isLoginModalOpen: PropTypes.bool,
  isModal: PropTypes.bool,
};

PhoneLoginForm.defaultProps = {
  initialValues: {},
  login: { login: {} },
  isModal: false,
};

export default createForm()(PhoneLoginForm);
